import { Abstract, Coordinates, Mark } from '@/types/commons/commons.types';

import { ClassificationEnum } from '../classification.types';

export enum SensorTypeEnum {
  ADSB = 'ADSB',
  AEROSCOPE = 'AEROSCOPE',
  ARDRONIS = 'ARDRONIS',
  AUTOMATON = 'AUTOMATON',
  AVISO = 'AVISO',
  BLAST = 'BLAST',
  DFB6 = 'DFB6',
  DRONE_SHIELD = 'DRONE_SHIELD',
  FLIR = 'FLIR',
  GROUND_STATION = 'GROUND_STATION',
  HOLODRONE = 'HOLODRONE',
  AF2206 = 'AF2206',
  AXIS = 'AXIS',
  MAJES = 'MAJES',
  ONVIF = 'ONVIF',
  ROBIN_MAX = 'ROBIN_MAX',
  SENID = 'SENID',
  SKY_EYE = 'SKY_EYE',
  SKY_JACKER = 'SKY_JACKER',
  SPEXER = 'SPEXER',
  XENTA = 'XENTA',
  ZMER = 'ZMER',
  GENERIC = 'GENERIC',
}

export const enum SensorLikeEnum {
  PERIMETER_CAMERA = 'PERIMETER_CAMERA',
}

export enum SubSensorTypeEnum {
  ARDRONIS_ANTENNA = 'ARDRONIS_ANTENNA',
  AUTOMATON_CABINET = 'AUTOMATON_CABINET',
  AUTOMATON_SEGMENT = 'AUTOMATON_SEGMENT',
  GENERIC_SUB_SENSOR = 'GENERIC_SUB_SENSOR',
  ROBIN_MAX_RADAR = 'ROBIN_MAX_RADAR',
  SENID_ANTENNA = 'SENID_ANTENNA',
  SKY_EYE_ANTENNA = 'SKY_EYE_ANTENNA',
  SKY_JACKER_ANTENNA = 'SKY_JACKER_ANTENNA',
  SPEXER_RADAR = 'SPEXER_RADAR',
}

export type ExtendedSensor = SensorTypeEnum | SensorLikeEnum | SubSensorTypeEnum;

export type SensorTypeEnumWithoutSection =
  | SensorTypeEnum
  | Exclude<SubSensorTypeEnum, SubSensorTypeEnum.AUTOMATON_SEGMENT>;

export interface HasUniqueKey {
  uniqueKey: string;
}

export interface HasEffectiveStatus {
  effectiveStatus: EffectiveSensorStatusEnum;
}

export interface HasHealthStatus {
  healthStatus: HealthStatusEnum;
  transmittedHealthStatus: HealthStatusEnum;
}

export interface HasVersion {
  version: string | null;
}

export interface PortConfiguration {
  port: number;
}

export interface HostConfiguration {
  host: string;
}

export interface RemoteHostConfiguration {
  remoteHost: string | null;
  remotePort: number | null;
}

export interface AlertTimeoutThreshold {
  warningNonDetectionTimeoutThreshold: number | null;
  nonDetectionTimeoutThreshold: number | null;
}

export interface CommonSubSensorConfiguration extends Abstract {
  id: number;
  uniqueKey: string;
  sensorId: number;
  name: string;
  code: string;
  site: string;
  appCode: string;
  active: boolean;
  maintenance: boolean;
  sensorColor: string;
  altitude: number | null;
  airportZone: string | null;
  stateOwnedZone: string | null;
  sector: string | null;
  infrastructure: string | null;
}

export interface PositionSubSensor extends CommonSubSensorConfiguration {
  position: Coordinates;
}

export interface CommonConfiguration extends Abstract, HasUniqueKey {
  id: number;
  site: string;
  appCode: string;
  marks: Mark[];
  code: string;
  name: string;
  sensorPosition: Coordinates;
  sensorAltitude: number;
  timeoutThreshold: number;
  trackDeleteTime: number;
  trackLostTime: number;
  defaultTargetClassification: ClassificationEnum;
  saveRawData: boolean;
  sensorColor: string | null;
  phoneNumber: string | null;
  active: boolean;
  maintenance: boolean;
  airportZone: string | null;
  stateOwnedZone: string | null;
  sector: string | null;
  infrastructure: string | null;
  horizontalPrecision: number | null;
  verticalPrecision: number | null;
}

export interface AsterixConfiguration {
  sac: number;
  sic: number;
  originSac: number | null;
  originSic: number | null;
}

export enum ConnectorStatusEnum {
  CONNECTED = 'CONNECTED',
  WAITING = 'WAITING',
  TIMEOUT = 'TIMEOUT',
  UNREACHABLE = 'UNREACHABLE',
}

export enum DataAvailabilityEnum {
  OK = 'OK',
  DESYNCHRONIZED = 'DESYNCHRONIZED',
  UNREADABLE = 'UNREADABLE',
  NO_DATA = 'NO_DATA',
}

export enum HealthStatusEnum {
  NA = 'NA',
  HEALTHY = 'HEALTHY',
  DEGRADED = 'DEGRADED',
  KO = 'KO',
}

export type EffectiveSensorStatusEnum =
  | Exclude<HealthStatusEnum, HealthStatusEnum.NA>
  | Extract<ConnectorStatusEnum, ConnectorStatusEnum.UNREACHABLE>;

export enum SpecificStateEnum {
  BT_DETECTION = 'BT_DETECTION',
  HT_DETECTION = 'HT_DETECTION',
  EFFRACTION = 'EFFRACTION',
  SLEEP = 'SLEEP',
  EMITTING = 'EMITTING',
  ROUND = 'ROUND',
  TRACKING = 'TRACKING',
  ROTATING = 'ROTATING',
  STOP_AND_STARE = 'STOP_AND_STARE',
  DISCONNECTED = 'DISCONNECTED',
  DETECTING = 'DETECTING',
  DEFENDING = 'DEFENDING',
  EXPIRED = 'EXPIRED',
  LOCKED = 'LOCKED',
  UNKNOWN = 'UNKNOWN',
}

export interface CommonSubSensorStatus extends HasUniqueKey, HasEffectiveStatus, HasHealthStatus {
  type: SubSensorTypeEnum;
  parentUniqueKey: string;
  position: Coordinates;
  name: string;
}

export interface HasSubSensorStatuses<T> {
  subSensorStatuses: T[];
}
export interface CommonSensorStatus extends HasUniqueKey, HasEffectiveStatus, HasHealthStatus {
  type: SensorTypeEnum;
  connectorStatus: ConnectorStatusEnum;
  dataAvailability: DataAvailabilityEnum;
  lastMessageDate: string;
  statusChangeDate: string;
}

export enum MetricStatusEnum {
  CRITICAL = 'CRITICAL',
  WARNING = 'WARNING',
  OK = 'OK',
}

export type MetricStatusValue = {
  value: number | null;
  state: MetricStatusEnum | null;
  criticalThreshold: number | null;
  warningThreshold: number | null;
};

export enum TrackingStatusEnum {
  DISABLED,
  PENDING,
  ACTIVE,
  LOST,
  STOP_BUFFERIZE,
  SCANNING,
  STOP_SCANNING,
}

export interface SensorTrackingStatus {
  globalSensorTrackingState: TrackingStatusEnum;
  currentTrackedObjectId: string | null;
  destination: Coordinates | null;
}

export interface TrackLight {
  id: string;
  uuid: string;
  site: string;
  sensorCode: string;
  subSensorCode?: string | null;
  sensorType: SensorTypeEnum;
  appCode: string;
  uniqueKey: string;
}
